/**
 * 文件路径: D:\XXY\MeiShan_Project\frontend\shared\api\enterprises.ts
 * 企业管理API服务
 * 
 * 更新记录：
 * - 2025-11-17 12:40: 添加 city_label, province_label, district_label 字段定义
 * - 2025-11-17 12:36: 修复 getEnterprises 方法，遵循 API URL 规范
 * - 2025-11-17 12:36: 添加 list() 方法作为 getEnterprises() 的别名
 */

import { BaseApiService } from './base'
import { httpClient } from './http'
import type { PaginatedResponse } from '../types'

// 企业基本信息接口
// ⚠️ 字段名与后端模型保持一致
export interface Enterprise {
  id: string
  name: string  // 企业名称（后端字段名）
  enterprise_name?: string  // 企业名称（兼容旧接口，某些列表API返回此字段）
  enterprise_address?: string  // 后端实际是 street_address（保留兼容）
  contract_status?: 'pending' | 'approved' | 'rejected' | 'expired'
  // ✅ 运营城市（M14新增）
  operator_city?: number | null  // 归属运营城市 ID
  operator_city_name?: string    // 归属运营城市名称（只读展示）
  // 行政区划代码
  city: string  // 行政区划代码（如 3101）
  province: string  // 行政区划代码（如 31）
  district?: string  // 前端别名（后端是 region）
  region?: string  // 后端实际字段名（行政区划代码）
  // ✅ 行政区划标签（只读，用于显示）
  city_label?: string  // 城市名称（如 "上海"）
  province_label?: string  // 省份名称（如 "上海"）
  district_label?: string  // 区县名称（如 "浦东新区"）
  street_address?: string  // 后端实际字段名
  workplace_count?: number
  employee_count?: number
  meal_segment_codes?: string[]  // ✅ 企业启用的餐段代码列表（标准字段）
  service_types?: string[]
  contact_person?: string
  contact_phone?: string
  description?: string
  enterprise_type?: string
  industry?: string
  estimated_employees?: number
  primary_contact_name?: string
  primary_contact_phone?: string
  operation_manager_name?: string
  operation_manager_phone?: string
  operation_manager_level?: string
  supervisor_name?: string
  supervisor_level?: string
  customer_service_representative?: string
  customer_service_phone?: string
  admin_name?: string
  admin_account?: string
  admin_phone?: string
  latitude?: number
  longitude?: number
  created_at?: string
  updated_at?: string
}

// 企业筛选条件
export interface EnterpriseFilters extends Record<string, unknown> {
  // ✅ 与 schema.yml 对齐：search/region/service_type_codes/meal_segments
  search?: string // 企业名称、联系人等模糊搜索（schema: search）
  operator_city?: number  // ✅ M14新增：归属运营城市筛选
  city?: string
  province?: string 
  region?: string
  meal_segments?: string[]
  contract_status?: string
  service_type_codes?: string[]
  created_at_after?: string
  created_at_before?: string
  consumption_type?: string
  // ---- 向后兼容（逐步移除）----
  keyword?: string // @deprecated -> search
  district?: string // @deprecated -> region
  service_types?: string[] // @deprecated -> service_type_codes
}

// 企业表单数据（分步骤）
export interface EnterpriseFormData {
  // 步骤1: 基本信息
  basic_info: {
    enterprise_name: string
    operator_city?: number  // ✅ M14新增：归属运营城市 ID
    enterprise_type?: string
    industry?: string
    estimated_employees?: number
    description?: string
    contact_person: string
    contact_phone: string
    contact_email?: string
    enterprise_address: string
    province: string
    city: string
    district: string
    is_admin?: boolean
    initial_account?: string
    initial_password?: string
    admin_name?: string
    admin_phone?: string
    operation_manager_name?: string
    operation_manager_phone?: string
    operation_manager_level?: string
    supervisor_name?: string
    supervisor_level?: string
    customer_service_representative?: string
    customer_service_phone?: string
    location?: { lat: number; lng: number; coord_type?: 'wgs84'|'gcj02'|'bd09' }
  }
  // 步骤2: 职场配置
  workplace_config: {
    workplaces: Array<{
      workplace_name: string
      province: string
      city: string
      district: string
      workplace_address: string
      estimated_people: number
      contact_person?: string
      contact_phone?: string
      location?: { lat: number; lng: number; coord_type?: 'wgs84'|'gcj02'|'bd09' }
      remarks?: string
    }>
  }
  // 步骤3: 餐段及时间规则
  meal_segment_config: {
    meal_segments: Record<string, {
      enabled: boolean
      order_start_time: string
      order_end_time: string
      delivery_start_time: string
      delivery_end_time: string
      advance_minutes: number
      max_orders_per_person: number
      applicable_workplaces: string[]
    }>
    enabled_segments?: string[]
    multi_order_per_segment: boolean
    multi_workplace_dining: boolean
  }
  // 步骤4: 商户餐厅配置
  merchant_config: {
    associated_merchants: string[]
    service_types: string[]
  }
  // 步骤5: 钱包与充值配置
  wallet_config: {
    initial_balance: number
    auto_recharge_enabled: boolean
    auto_recharge_threshold: number
    auto_recharge_amount: number
    subsidy_method: string
    clearing_method: string
  }
  // 步骤6: 合同与解析
  contract_config: {
    contract_template_id?: string
    contract_file?: File
    payment_cycle: number
    commission_rate: number
    invoice_required: boolean
  }
  // 步骤7: 其他功能配置
  other_config: {
    employee_level_tags_enabled: boolean
    smart_cabinet_enabled: boolean
    smart_cabinet_count: number
    special_permissions: string[]
  }
}

/**
 * 企业管理API服务类
 * 提供企业相关的所有API操作
 */
class EnterpriseApiService extends BaseApiService<Enterprise, EnterpriseFormData, Partial<EnterpriseFormData>> {
  protected readonly endpoint = 'enterprises/'

  /**
   * 获取企业列表（支持筛选）
   * @param params - 查询和筛选参数
   * @returns 分页响应数据
   */
  async getEnterprises(params?: EnterpriseFilters & { page?: number; page_size?: number }): Promise<PaginatedResponse<Enterprise>> {
    try {
      const mappedParams: Record<string, unknown> = { ...(params as Record<string, unknown> | undefined) }
      // 兼容旧字段名（但对外契约以 schema.yml 为准）
      if (typeof mappedParams.search !== 'string' && typeof mappedParams.keyword === 'string' && mappedParams.keyword.trim()) {
        mappedParams.search = mappedParams.keyword.trim()
      }
      if (typeof mappedParams.region !== 'string' && (mappedParams.district !== undefined && mappedParams.district !== null) && String(mappedParams.district).trim()) {
        mappedParams.region = String(mappedParams.district).trim()
      }
      if (!Array.isArray(mappedParams.service_type_codes) && Array.isArray(mappedParams.service_types)) {
        mappedParams.service_type_codes = mappedParams.service_types
      }
      delete mappedParams.keyword
      delete mappedParams.district
      delete mappedParams.service_types

      // ✅ 遵循 20-frontend-api-url-standards.mdc 规范
      // 使用 httpClient.get()，自动提取 data 字段
      return await httpClient.get<PaginatedResponse<Enterprise>>(this.endpoint, { params: mappedParams })
    } catch (error: unknown) {
      console.error('[企业API] 获取企业列表失败:', error)
      const err = error as { response?: { status?: number; data?: { message?: string } } }
      // 提供更详细的错误信息
      if (err.response?.status === 401) {
        throw new Error('身份认证失败，请重新登录')
      } else if (err.response?.status === 403) {
        throw new Error('权限不足，无法访问企业数据')
      } else if (typeof err.response?.status === 'number' && err.response.status >= 500) {
        throw new Error('服务器内部错误，请稍后重试')
      } else if (err.response?.data?.message) {
        throw new Error(err.response.data.message)
      }
      throw new Error('获取企业列表失败')
    }
  }

  /**
   * 获取企业列表（list 别名，用于简化调用）
   * @param params - 查询和筛选参数
   * @returns 分页响应数据
   */
  async list(params?: EnterpriseFilters & { page?: number; page_size?: number }): Promise<PaginatedResponse<Enterprise>> {
    return await this.getEnterprises(params)
  }

  /**
   * 获取企业详情
   * @param id - 企业ID
   * @returns 企业详情数据
   */
  async getEnterpriseDetail(id: string): Promise<Enterprise> {
    return await this.getById(id)
  }

  /**
   * 创建新企业
   * @param data - 企业表单数据
   * @returns 创建的企业数据
   */
  async createEnterprise(data: EnterpriseFormData): Promise<Enterprise> {
    // 扁平化 basic_info 为后端可接受的基础信息字段
    const bi = (data.basic_info || {}) as Partial<EnterpriseFormData['basic_info']>
    const biExt = bi as unknown as Record<string, unknown>
    const payload: Record<string, unknown> = {
      // 名称与地址映射
      name: bi.enterprise_name,
      province: bi.province,
      city: bi.city,
      region: bi.district,
      street_address: bi.enterprise_address,
      // 主联系人映射
      contact_person: bi.contact_person,
      contact_phone: bi.contact_phone,
      contact_email: bi.contact_email,
      // 管理员/账号信息
      is_admin: bi.is_admin,
      initial_account: bi.initial_account,
      initial_password: bi.initial_password,
      admin_name: bi.admin_name,
      admin_phone: bi.admin_phone,
      // 运营信息
      operation_manager_name: bi.operation_manager_name,
      operation_manager_phone: bi.operation_manager_phone,
      operation_manager_level: bi.operation_manager_level,
      supervisor_name: bi.supervisor_name,
      supervisor_level: bi.supervisor_level,
      customer_service_representative: bi.customer_service_representative,
      customer_service_phone: bi.customer_service_phone,
      // 其他基础扩展
      enterprise_type: bi.enterprise_type,
      industry: bi.industry,
      estimated_employees: bi.estimated_employees,
    }
    // 统一常量口径：从基础服务设置映射到 *_codes（服务类型=消费类型；餐段）
    if (Array.isArray(biExt.consumption_scenes)) {
      payload.service_type_codes = [...(biExt.consumption_scenes as unknown[])]
    }
    if (Array.isArray(biExt.enabled_meal_periods)) {
      payload.meal_segment_codes = [...(biExt.enabled_meal_periods as unknown[])]
    }
    if (bi?.location && typeof bi.location.lat === 'number' && typeof bi.location.lng === 'number') {
      payload.location = {
        lat: bi.location.lat,
        lng: bi.location.lng,
        coord_type: bi.location.coord_type || 'gcj02',
      }
    }
    return await httpClient.post<Enterprise>(this.endpoint, payload)
  }

  /**
   * 更新企业信息
   * @param id - 企业ID
   * @param data - 更新的企业数据
   * @returns 更新后的企业数据
   */
  async updateEnterprise(id: string, data: Partial<EnterpriseFormData>): Promise<Enterprise> {
    // 统一改为对企业对象本身做 PATCH，支持 *_codes 与基础信息同时更新
    const bi = (data.basic_info || {}) as Partial<EnterpriseFormData['basic_info']>
    const biExt = bi as unknown as Record<string, unknown>
    const payload: Record<string, unknown> = {}
    if (typeof bi.enterprise_name === 'string' && bi.enterprise_name) {
      payload.name = bi.enterprise_name
    }
    if (bi.province != null) payload.province = bi.province
    if (bi.city != null) payload.city = bi.city
    if (bi.district != null) payload.region = bi.district
    if (bi.enterprise_address != null) payload.street_address = bi.enterprise_address
    if (bi.contact_person != null) payload.contact_person = bi.contact_person
    if (bi.contact_phone != null) payload.contact_phone = bi.contact_phone
    if (bi.contact_email != null) payload.contact_email = bi.contact_email
    if (bi.admin_name != null) payload.admin_name = bi.admin_name
    if (bi.admin_phone != null) payload.admin_phone = bi.admin_phone
    if (bi.operation_manager_name != null) payload.operation_manager_name = bi.operation_manager_name
    if (bi.operation_manager_phone != null) payload.operation_manager_phone = bi.operation_manager_phone
    if (bi.operation_manager_level != null) payload.operation_manager_level = bi.operation_manager_level
    if (bi.supervisor_name != null) payload.supervisor_name = bi.supervisor_name
    if (bi.supervisor_level != null) payload.supervisor_level = bi.supervisor_level
    if (bi.customer_service_representative != null) payload.customer_service_representative = bi.customer_service_representative
    if (bi.customer_service_phone != null) payload.customer_service_phone = bi.customer_service_phone
    if (bi.estimated_employees != null) payload.estimated_employees = bi.estimated_employees
    if (bi.description != null) payload.description = bi.description
    if (bi?.location && typeof bi.location.lat === 'number' && typeof bi.location.lng === 'number') {
      payload.location = {
        lat: bi.location.lat,
        lng: bi.location.lng,
        coord_type: bi.location.coord_type || 'gcj02',
      }
    }
    // 同步 codes
    if (Array.isArray(biExt.consumption_scenes)) {
      payload.service_type_codes = [...(biExt.consumption_scenes as unknown[])]
    }
    if (Array.isArray(biExt.enabled_meal_periods)) {
      payload.meal_segment_codes = [...(biExt.enabled_meal_periods as unknown[])]
    }
    // v3.0 用户名策略
    if (biExt.custom_username_prefix !== undefined) payload.custom_username_prefix = biExt.custom_username_prefix
    if (biExt.username_prefix_type) payload.username_prefix_type = biExt.username_prefix_type
    if (biExt.username_suffix_type) payload.username_suffix_type = biExt.username_suffix_type
    if (biExt.username_custom_suffix !== undefined) payload.username_custom_suffix = biExt.username_custom_suffix
    return await httpClient.patch<Enterprise>(`${this.endpoint}${id}/`, payload)
  }

  /**
   * 删除企业
   * @param id - 企业ID
   */
  async deleteEnterprise(id: string): Promise<void> {
    return await this.delete(id)
  }

  /**
   * 批量操作企业
   * @param operation - 操作类型
   * @param enterpriseIds - 企业ID数组
   */
  async batchOperation(operation: 'approve' | 'reject' | 'disable' | 'enable', enterpriseIds: string[]): Promise<void> {
    return await httpClient.post(`${this.endpoint}batch-operation/`, {
      operation,
      enterprise_ids: enterpriseIds
    })
  }

  /**
   * 导出企业数据
   * @param params - 导出参数
   * @param format - 导出格式
   * @returns 文件blob
   */
  async exportEnterprises(params?: { ids?: string[] } & EnterpriseFilters, format: 'csv' | 'xlsx' | 'pdf' = 'xlsx'): Promise<Blob> {
    return await this.export(params, format)
  }

  /**
   * 获取企业统计信息
   * @param params - 统计参数
   * @returns 统计数据
   */
  async getEnterpriseStats(params?: EnterpriseFilters): Promise<Record<string, unknown>> {
    return await this.getStats(params)
  }

  /**
   * 搜索企业
   * @param query - 搜索关键词
   * @param params - 额外参数
   * @returns 搜索结果
   */
  async searchEnterprises(query: string, params?: EnterpriseFilters): Promise<PaginatedResponse<Enterprise>> {
    return await this.search(query, params)
  }

  /**
   * 获取企业餐段配置
   * @param id - 企业ID
   * @returns 餐段配置数据
   */
  async getMealSegmentsConfig(id: string): Promise<unknown> {
    return await httpClient.get(`${this.endpoint}${id}/meal-segments/`)
  }

  /**
   * 更新企业餐段配置
   * @param id - 企业ID
   * @param data - 餐段配置数据
   * @returns 更新结果
   */
  async updateMealSegmentsConfig(id: string, data: unknown): Promise<unknown> {
    return await httpClient.patch(`${this.endpoint}${id}/meal-segments/`, data)
  }

  /**
   * 获取企业补贴配置
   * @param id - 企业ID
   * @returns 补贴配置数据
   */
  async getSubsidyConfig(id: string): Promise<unknown> {
    return await httpClient.get(`${this.endpoint}${id}/subsidy-config/`)
  }

  /**
   * 更新企业补贴配置
   * @param id - 企业ID
   * @param data - 补贴配置数据
   * @returns 更新结果
   */
  async updateSubsidyConfig(id: string, data: unknown): Promise<unknown> {
    return await httpClient.patch(`${this.endpoint}${id}/subsidy-config/`, data)
  }

  /**
   * 获取企业智能餐柜配置
   * @param id - 企业ID
   * @returns 餐柜配置数据
   */
  async getSmartCabinetConfig(id: string): Promise<unknown> {
    return await httpClient.get(`${this.endpoint}${id}/smart-cabinet-config/`)
  }

  /**
   * 更新企业智能餐柜配置
   * @param id - 企业ID
   * @param data - 餐柜配置数据
   * @returns 更新结果
   */
  async updateSmartCabinetConfig(id: string, data: unknown): Promise<unknown> {
    return await httpClient.patch(`${this.endpoint}${id}/smart-cabinet-config/`, data)
  }

  // ==================== 新增 API 方法 ====================

  /**
   * 获取企业餐段时间规则
   * @param id - 企业ID
   * @returns 餐段时间规则配置
   */
  async getMealTimeRules(id: string): Promise<MealTimeRuleDTO[]> {
    const resp = await httpClient.get<MealTimeRuleDTO[]>(`${this.endpoint}${id}/meal-time-rules/`)
    return Array.isArray(resp) ? resp : []
  }

  /**
   * 获取企业补贴汇总
   * @param id - 企业ID
   * @returns 补贴汇总信息
   */
  async getSubsidySummary(id: string): Promise<EnterpriseSubsidySummary> {
    return await httpClient.get<EnterpriseSubsidySummary>(`${this.endpoint}${id}/subsidy-summary/`)
  }

  /**
   * 获取员工导入模板
   * @param id - 企业ID
   * @returns 模板文件 Blob
   */
  async getEmployeeImportTemplate(id: string): Promise<Blob> {
    return await httpClient.get(`${this.endpoint}${id}/employees/import-template/`, {
      responseType: 'blob'
    }) as unknown as Blob
  }

  /**
   * 批量导入员工
   * @param id - 企业ID
   * @param file - Excel 文件
   * @returns 导入结果
   */
  async batchImportEmployees(id: string, file: File): Promise<EmployeeImportResult> {
    const formData = new FormData()
    formData.append('file', file)
    return await httpClient.post<EmployeeImportResult>(
      `${this.endpoint}${id}/employees/batch-import/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
  }

  /**
   * 批量导出员工
   * @param id - 企业ID
   * @param params - 导出参数
   * @returns 导出文件 Blob
   */
  async batchExportEmployees(
    id: string,
    params?: { department_id?: string; status?: string }
  ): Promise<Blob> {
    return await httpClient.post(
      `${this.endpoint}${id}/employees/batch-export/`,
      params || {},
      { responseType: 'blob' }
    ) as unknown as Blob
  }

  /**
   * 获取企业服务配置
   * @param id - 企业ID
   * @returns 服务配置数据
   */
  async getServiceConfig(id: string): Promise<unknown> {
    return await httpClient.get(`${this.endpoint}${id}/service-config/`)
  }

  /**
   * 更新企业服务配置
   * @param id - 企业ID
   * @param data - 服务配置数据
   * @returns 更新结果
   */
  async updateServiceConfig(id: string, data: unknown): Promise<unknown> {
    return await httpClient.patch(`${this.endpoint}${id}/service-config/`, data)
  }

  /**
   * 获取企业工作日历
   * @param id - 企业ID
   * @param year - 年份
   * @returns 日历数据
   */
  async getWorkCalendar(id: string, year?: number): Promise<EnterpriseCalendar> {
    const params = year ? { year } : {}
    return await httpClient.get<EnterpriseCalendar>(`${this.endpoint}${id}/work-calendar/`, { params })
  }

  /**
   * 更新企业工作日历
   * @param id - 企业ID
   * @param data - 日历数据
   * @returns 更新结果
   */
  async updateWorkCalendar(id: string, data: Partial<EnterpriseCalendar>): Promise<EnterpriseCalendar> {
    return await httpClient.patch<EnterpriseCalendar>(`${this.endpoint}${id}/work-calendar/`, data)
  }
}

// ==================== 新增类型定义 ====================

/**
 * 餐段时间规则
 */
export interface MealTimeRuleDTO {
  id: number | string
  meal_segment_code: string
  meal_segment_label?: string
  order_start_time: string
  order_end_time: string
  delivery_start_time?: string
  delivery_end_time?: string
  advance_order_minutes?: number
  is_active: boolean
  workplace_id?: number | string
  workplace_name?: string
}

/**
 * 企业补贴汇总
 */
export interface EnterpriseSubsidySummary {
  total_wallets: number
  total_employees: number
  total_subsidy_balance: string
  total_personal_balance: string
  active_batches: number
  pending_batches: number
  this_month_granted: string
  this_month_consumed: string
  utilization_rate: number
}

/**
 * 员工导入结果
 */
export interface EmployeeImportResult {
  success: boolean
  total_rows: number
  success_count: number
  failed_count: number
  errors?: Array<{
    row: number
    field: string
    message: string
  }>
}

/**
 * 企业日历
 */
export interface EnterpriseCalendar {
  id?: number | string
  enterprise: number | string
  year: number
  holidays: string[]  // 节假日日期列表 YYYY-MM-DD
  workdays: string[]  // 调休工作日列表 YYYY-MM-DD
  created_at?: string
  updated_at?: string
}

// 导出企业API服务实例
export const enterpriseApi = new EnterpriseApiService()
